import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ASSETS } from '../config/assets'

interface HeaderProps {
  apiStatus: 'loading' | 'connected' | 'error'
}

export default function Header({ apiStatus }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const getStatusColor = () => {
    switch (apiStatus) {
      case 'connected': return 'bg-green-500'
      case 'error': return 'bg-red-500'
      default: return 'bg-yellow-500'
    }
  }

  const getStatusText = () => {
    switch (apiStatus) {
      case 'connected': return 'API Connected'
      case 'error': return 'API Disconnected'
      default: return 'Connecting...'
    }
  }

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    
    // Redirect to login
    navigate('/login')
  }

  // Check if user is logged in and get user info
  const isLoggedIn = !!localStorage.getItem('auth_token')
  const user = isLoggedIn ? JSON.parse(localStorage.getItem('user') || '{}') : null
  const isAdmin = user?.role === 'admin'

  // Helper function to get nav link classes
  const getNavLinkClasses = (path: string) => {
    const isActive = location.pathname === path
    return isActive
      ? "text-blue-600 hover:text-blue-800 px-3 py-2 text-sm font-medium transition-colors"
      : "text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
  }

  // Helper function for mobile nav link classes
  const getMobileNavLinkClasses = (path: string) => {
    const isActive = location.pathname === path
    return isActive
      ? "block px-3 py-2 text-base font-medium text-blue-600 hover:text-blue-800"
      : "block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900"
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                src={ASSETS.LOGO_CORE} 
                alt="The Core Briefing" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {isLoggedIn ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={getNavLinkClasses('/dashboard')}
                >
                  Dashboard
                </Link>
                                  <Link
                    to="/core-briefing"
                    className={getNavLinkClasses('/core-briefing')}
                  >
                    The Core Briefing
                  </Link>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className={getNavLinkClasses('/admin')}
                  >
                    Admin
                  </Link>
                )}
                <button 
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link 
                to="/login" 
                className={getNavLinkClasses('/login')}
              >
                Login
              </Link>
            )}
          </nav>

          {/* API Status Indicator */}
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
            <span className="text-xs text-gray-500">{getStatusText()}</span>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-t border-gray-200">
        <div className="px-2 pt-2 pb-3 space-y-1">
          {isLoggedIn ? (
            <>
              <Link 
                to="/dashboard" 
                className={getMobileNavLinkClasses('/dashboard')}
              >
                Dashboard
              </Link>
                              <Link
                  to="/core-briefing"
                  className={getMobileNavLinkClasses('/core-briefing')}
                >
                  The Core Briefing
                </Link>
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className={getMobileNavLinkClasses('/admin')}
                >
                  Admin
                </Link>
              )}
              <button 
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900"
              >
                Logout
              </button>
            </>
          ) : (
            <Link 
              to="/login" 
              className={getMobileNavLinkClasses('/login')}
            >
              Login
            </Link>
          )}
        </div>
      </div>
    </header>
  )
} 